<script setup lang="ts">
  defineOptions({ name: 'EventsDetailSection' })

  import { computed, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { Image01Icon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'
  import OptimisedImage from '@common/media-layer/components/OptimisedImage.vue'

  import CPButton from '@ui/components/CPButton.vue'

  import EventDetailAboutSection from './EventDetailAboutSection.vue'
  import EventDetailAgendaSection from './EventDetailAgendaSection.vue'
  import EventDetailAttendeesSection from './EventDetailAttendeesSection.vue'
  import EventDetailExhibitorsSection from './EventDetailExhibitorsSection.vue'
  import EventDetailSectionNav from './EventDetailSectionNav.vue'
  import EventDetailSpeakersSection from './EventDetailSpeakersSection.vue'
  import EventDetailTicketsSection from './EventDetailTicketsSection.vue'
  import EventsDetailSectionSkeleton from './EventsDetailSectionSkeleton.vue'
  import SectionEmptyState from './molecules/SectionEmptyState.vue'

  import { useEventDetailTab } from '../composables/useEventDetailTab'

  import type { EventDetailNavTab, EventDetailSectionId, EventDetailViewModel } from '../types/event-detail'
  import type { EventsExhibitorDetailResource, EventsSpeakerDetailResource } from '../types/events-detail-experience-controller'
  import type { EventsHubResourceState } from '../types/events-hub'

  const props = defineProps({
    state: {
      type: String as PropType<EventsHubResourceState>,
      required: true,
    },
    event: {
      type: Object as PropType<EventDetailViewModel | null>,
      default: null,
    },
    defaultTab: {
      type: String as PropType<EventDetailSectionId>,
      default: 'about',
    },
    gap: {
      type: String as PropType<'sm' | 'md' | 'lg'>,
      default: 'md',
    },
    sidebarWidth: {
      type: String as PropType<'280' | '320' | '360'>,
      default: '320',
    },
    showAbout: {
      type: Boolean,
      default: true,
    },
    showAgenda: {
      type: Boolean,
      default: true,
    },
    showSidebar: {
      type: Boolean,
      default: true,
    },
    showSpeakers: {
      type: Boolean,
      default: true,
    },
    showAttendees: {
      type: Boolean,
      default: true,
    },
    showExhibitors: {
      type: Boolean,
      default: true,
    },
    showTickets: {
      type: Boolean,
      default: true,
    },
    isRegistered: {
      type: Boolean,
      default: false,
    },
    speakerDetail: {
      type: Object as PropType<EventsSpeakerDetailResource>,
      default: () => ({
        state: 'empty' as const,
        speakerId: '',
        bio: '',
        links: [],
        sessions: [],
      }),
    },
    exhibitorDetail: {
      type: Object as PropType<EventsExhibitorDetailResource>,
      default: () => ({
        state: 'empty' as const,
        representatives: [],
      }),
    },
    idPrefix: {
      type: String,
      default: 'event-detail',
    },
  })

  const emit = defineEmits<{
    saveSession: [sessionId: string]
    addToCalendar: [sessionId: string]
    register: []
    downloadTicket: [ticketId: string]
    chat: [attendeeId: string]
    openSpeaker: [speakerId: string]
    closeSpeaker: []
    openExhibitor: [exhibitorId: string]
    closeExhibitor: []
  }>()

  const { t } = useI18n()

  const ALL_TABS: readonly EventDetailNavTab[] = [
    { id: 'about', labelKey: 'clientportal.events.detail.nav.about' },
    { id: 'speakers', labelKey: 'clientportal.events.detail.nav.speakers' },
    { id: 'attendees', labelKey: 'clientportal.events.detail.nav.attendees' },
    { id: 'exhibitors', labelKey: 'clientportal.events.detail.nav.exhibitors' },
    { id: 'tickets', labelKey: 'clientportal.events.detail.nav.tickets' },
  ]

  const UNREGISTERED_TAB_IDS: readonly EventDetailSectionId[] = ['about', 'speakers']

  const visibleSections = computed(() => props.event?.visibleSections)
  const canShowAbout = computed(() => props.showAbout && (visibleSections.value?.about ?? true))
  const canShowAgenda = computed(() => props.showAgenda && (visibleSections.value?.agenda ?? true))
  const canShowSpeakers = computed(() => props.showSpeakers && (visibleSections.value?.speakers ?? true))
  const canShowAttendees = computed(() => props.showAttendees || (visibleSections.value?.attendees ?? true))
  const canShowExhibitors = computed(() => props.showExhibitors && (visibleSections.value?.exhibitors ?? true))
  const canShowTickets = computed(() => props.showTickets && (visibleSections.value?.tickets ?? true))

  const showOverviewTab = computed(() => canShowAbout.value || canShowAgenda.value)

  const tabs = computed(() =>
    ALL_TABS.filter(tab => {
      const authorEnabled =
        tab.id === 'about'
          ? showOverviewTab.value
          : tab.id === 'tickets'
            ? canShowTickets.value
            : tab.id === 'speakers'
              ? canShowSpeakers.value
              : tab.id === 'attendees'
                ? canShowAttendees.value
                : canShowExhibitors.value
      return authorEnabled && (props.isRegistered || UNREGISTERED_TAB_IDS.includes(tab.id))
    })
  )

  const canRegister = computed(() => Boolean(props.event?.eventUrl) && !props.isRegistered)
  const showCoverImage = computed(() => Boolean(props.event?.imageUrl?.trim()))

  const gapClass = computed(() => {
    const map = { sm: 'gap-2', md: 'gap-4', lg: 'gap-6' } as const
    return map[props.gap]
  })

  const visibleTabIds = computed(() => tabs.value.map(tab => tab.id))
  const { preferredTab, selectTab: navigateToTab } = useEventDetailTab(() => props.defaultTab)

  const resolveActiveTab = (preferred: EventDetailSectionId): EventDetailSectionId | null => {
    if (visibleTabIds.value.includes(preferred)) return preferred
    return visibleTabIds.value[0] ?? null
  }

  const activeTabId = computed(() => resolveActiveTab(preferredTab.value))

  const selectTab = (tabId: EventDetailSectionId) => {
    if (!visibleTabIds.value.includes(tabId)) return
    void navigateToTab(tabId)
  }
</script>

<template>
  <section :id="createElementId(idPrefix, 'section', 'root')" class="flex w-full flex-col">
    <EventsDetailSectionSkeleton
      v-if="state === 'loading'"
      :gap="gap"
      :show-about="showAbout"
      :show-agenda="showAgenda"
      :id-prefix="idPrefix"
    />

    <SectionEmptyState
      v-else-if="state === 'error'"
      :id="createElementId(idPrefix, 'empty-state', 'error')"
      :title="t('clientportal.events.detail.loadErrorTitle')"
      :description="t('clientportal.events.detail.loadErrorDescription')"
    />

    <SectionEmptyState
      v-else-if="state === 'empty' || !event"
      :id="createElementId(idPrefix, 'empty-state', 'not-found')"
      :title="t('clientportal.events.detail.notFoundTitle')"
      :description="t('clientportal.events.detail.notFoundDescription')"
    />

    <SectionEmptyState
      v-else-if="!activeTabId"
      :id="createElementId(idPrefix, 'empty-state', 'no-sections')"
      :title="t('clientportal.events.detail.noVisibleSectionsTitle')"
      :description="t('clientportal.events.detail.noVisibleSectionsDescription')"
    />

    <template v-else>
      <div class="mx-auto w-full max-w-[82.5rem] px-4 pt-6 md:px-6">
        <div
          :id="createElementId(idPrefix, 'chrome', 'banner')"
          class="event-detail-chrome-banner relative aspect-[2/1] w-full overflow-hidden rounded-xl bg-[var(--cp-primary-100)] md:aspect-auto md:h-[30rem] shadow-md"
        >
          <div class="absolute inset-0">
            <OptimisedImage
              v-if="showCoverImage"
              :id="createElementId(idPrefix, 'chrome', 'banner-image')"
              :src="event.imageUrl"
              :alt="event.title"
              source="client-portal"
              :use-raw-url="true"
              :contain="true"
              gradient-mode="always"
              loading="eager"
              fetchpriority="high"
            />
            <div v-else class="flex h-full w-full items-center justify-center text-[var(--cp-primary-300)]" aria-hidden="true">
              <Image01Icon class="h-10 w-10" />
            </div>
          </div>
        </div>
      </div>

      <div class="sticky top-0 z-20 mt-5 w-full border-b border-[var(--cp-border-200)] bg-surface">
        <div class="mx-auto flex w-full max-w-[82.5rem] items-center justify-between gap-4 px-4 md:px-6">
          <EventDetailSectionNav :tabs="tabs" :active-tab-id="activeTabId" :id-prefix="idPrefix" @select-tab="selectTab" />
          <div v-if="canRegister" :id="createElementId(idPrefix, 'chrome', 'actions')" class="flex shrink-0 items-center gap-2 pb-1">
            <CPButton
              :id="createElementId(idPrefix, 'chrome', 'register')"
              type="primary"
              color="primary"
              size="sm"
              @click="emit('register')"
            >
              {{ t('clientportal.events.detail.register') }}
            </CPButton>
          </div>
        </div>
      </div>

      <div v-if="activeTabId === 'about'" class="mx-auto w-full max-w-[82.5rem] px-4 py-6 md:px-6">
        <div :id="createElementId(idPrefix, 'about-tab', 'root')" class="flex min-w-0 flex-col" :class="gapClass">
          <EventDetailAboutSection
            :title="event.title"
            :about="canShowAbout ? event.about : ''"
            :organizer-name="event.organizerName"
            :organizer-email="event.organizerEmail"
            :organizer-phone="event.organizerPhone"
            :organizer-website="event.organizerWebsite"
            :organizer-image-url="event.organizerImageUrl"
            :date-label="event.dateLabel"
            :time-label="event.timeLabel"
            :location-label="event.locationLabel"
            :format-kind="event.formatKind"
            :id-prefix="idPrefix"
          />
          <EventDetailAgendaSection
            v-if="canShowAgenda"
            :summary="event.agendaSummary"
            :days="event.agendaDays"
            :is-registered="isRegistered"
            :id-prefix="idPrefix"
            @save-session="emit('saveSession', $event)"
            @add-to-calendar="emit('addToCalendar', $event)"
            @open-speaker="emit('openSpeaker', $event)"
          />
        </div>
      </div>

      <EventDetailTicketsSection
        v-else-if="activeTabId === 'tickets'"
        :tickets="event.tickets"
        :event-title="event.title"
        :date-label="event.dateLabel"
        :time-label="event.timeLabel ?? ''"
        :location-venue="event.locationVenue || event.locationLabel"
        :location-address="event.locationAddress ?? ''"
        :id-prefix="idPrefix"
        @download-ticket="emit('downloadTicket', $event)"
      />
      <EventDetailSpeakersSection
        v-else-if="activeTabId === 'speakers'"
        :speakers="event.speakers"
        :speaker-detail="speakerDetail"
        :id-prefix="idPrefix"
        @open-speaker="emit('openSpeaker', $event)"
        @close-speaker="emit('closeSpeaker')"
        @add-to-calendar="emit('addToCalendar', $event)"
      />
      <EventDetailAttendeesSection
        v-else-if="activeTabId === 'attendees'"
        :attendees="event.attendees"
        :attendees-total-count="event.attendeesTotalCount"
        :id-prefix="idPrefix"
        @chat="emit('chat', $event)"
      />
      <EventDetailExhibitorsSection
        v-else-if="activeTabId === 'exhibitors'"
        :exhibitors="event.exhibitors"
        :exhibitor-detail="exhibitorDetail"
        :id-prefix="idPrefix"
        @open-exhibitor="emit('openExhibitor', $event)"
        @close-exhibitor="emit('closeExhibitor')"
      />
    </template>
  </section>
</template>

<style scoped>
  .event-detail-chrome-banner :deep(#thumbnail-image-main) {
    object-fit: cover;
  }

  @media (min-width: 768px) {
    .event-detail-chrome-banner :deep(#thumbnail-image-main) {
      object-fit: contain;
    }
  }
</style>
